import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  Empty,
  Form,
  Input,
  Modal,
  Spin,
  Typography,
  Upload,
  type UploadProps,
  notification,
} from 'antd'
import {
  CustomerServiceOutlined,
  PlusOutlined,
  PlayCircleOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import ImgCrop from 'antd-img-crop'
import { useTranslation } from 'react-i18next'

import { useAudioPlayer } from '../../contexts/AudioPlayerContext'
import { apiBaseURL } from '../../utils/api'
import { useAppDispatch, useAppSelector } from '../../utils/redux/store'
import type { Song } from '../../utils/redux/playlistSlice'
import {
  attemptCreateUserPlaylist,
  attemptGetUserPlaylists,
  type UserPlaylist,
} from '../../utils/redux/userPlaylistsSlice'
import './UserPlaylists.scss'

const { Title, Text } = Typography

type PlaylistFormValues = {
  title: string
  description?: string
}

function getUploadedFilename(response: unknown) {
  if (typeof response === 'string') return response
  if (response && typeof response === 'object' && 'filename' in response) {
    return String((response as { filename?: string }).filename ?? '')
  }

  return ''
}

function getPlaylistImageUrl(image?: string) {
  if (!image) return ''
  const clean = String(image)
    .replace(/^\/+/, '')
    .replace(/^playlists\//, '')
    
  return `${process.env.REACT_APP_UPLOADED_BASE_URL}/playlists/${clean}`
  // return `${apiBaseURL}/uploaded/playlists/${clean}`
}

function getSongs(playlist: UserPlaylist): Song[] {
  return (playlist.songs ?? []).filter((song) => song?.file) as Song[]
}

export default function UserPlaylists() {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { playQueue } = useAudioPlayer()
  const { items, isLoading, isSaving } = useAppSelector((state) => state.userPlaylists)
  const { token } = useAppSelector((state) => state.auth)

  const [createOpen, setCreateOpen] = useState(false)
  const [artworkImage, setArtworkImage] = useState('')
  const [isArtworkUploading, setIsArtworkUploading] = useState(false)
  const [createForm] = Form.useForm<PlaylistFormValues>()

  useEffect(() => {
    void dispatch(attemptGetUserPlaylists())
  }, [dispatch])

  const sortedItems = useMemo(
    () => [...items].sort((a, b) => Number(b.createdAt ?? 0) - Number(a.createdAt ?? 0)),
    [items],
  )

  const closeCreateModal = () => {
    setCreateOpen(false)
    createForm.resetFields()
    setArtworkImage('')
    setIsArtworkUploading(false)
  }

  const uploadProps: UploadProps = {
    name: 'file',
    multiple: false,
    showUploadList: false,
    accept: 'image/jpeg, image/png, image/jpg',
    action: `${apiBaseURL}/user-playlists/upload`,
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    onChange: (info) => {
      if (info.file.status === 'uploading') {
        setIsArtworkUploading(true)
        return
      }

      setIsArtworkUploading(false)

      if (info.file.status === 'done') {
        const filename = getUploadedFilename(info.file.response)
        setArtworkImage(filename)
      } else if (info.file.status === 'error') {
        const statusCode = (info.file.response as { statusCode?: number } | undefined)
          ?.statusCode

        notification.error({
          message: t('playlists.notifications.title', { defaultValue: 'Playlists' }),
          description:
            statusCode === 401
              ? t('playlists.errors.sessionExpired', {
                  defaultValue: 'Your session has expired, please re login and try again',
                })
              : t('playlists.errors.uploadFailed', {
                  defaultValue: `Could not upload ${info.file.name} file at this moment`,
                }),
        })
      }
    },
  }

  const onCreatePlaylist = async () => {
    try {
      const values = await createForm.validateFields()

      await dispatch(
        attemptCreateUserPlaylist({
          title: values.title,
          description: values.description,
          image: artworkImage || undefined,
        }),
      ).unwrap()

      closeCreateModal()
    } catch (error: any) {
      if (error?.errorFields) return

      notification.error({
        message: t('playlists.notifications.title', { defaultValue: 'Playlists' }),
        description:
          error?.message ||
          t('playlists.errors.createFailed', {
            defaultValue: 'Failed to create playlist',
          }),
      })
    }
  }

  const playPlaylist = (playlist: UserPlaylist) => {
    const songs = getSongs(playlist)
    if (!songs.length) return
    playQueue(songs, 0)
  }

  return (
    <div className="userPlaylistsPage">
      <div className="userPlaylistsHero">
        <div>
          <Title level={2} className="userPlaylistsTitle">
            {t('playlists.title', { defaultValue: 'My Playlists' })}
          </Title>
          <Text className="userPlaylistsSubtitle">
            {t('playlists.subtitle', {
              defaultValue: 'Create and manage playlists you own.',
            })}
          </Text>
        </div>

        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setCreateOpen(true)}
        >
          {t('playlists.create', { defaultValue: 'Create playlist' })}
        </Button>
      </div>

      {isLoading ? (
        <div className="userPlaylistsEmptyState">
          <Spin size="large" />
        </div>
      ) : sortedItems.length ? (
        <div className="userPlaylistsGrid">
          {sortedItems.map((playlist) => {
            const imageUrl = getPlaylistImageUrl(playlist.image)
            const songs = getSongs(playlist)

            return (
              <div
                key={playlist._id}
                className="userPlaylistCard"
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/playlists/${playlist._id}`)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    navigate(`/playlists/${playlist._id}`)
                  }
                }}
              >
                <div className="userPlaylistCardArtwork">
                  {imageUrl ? (
                    <img src={imageUrl} alt={playlist.title} loading="lazy" />
                  ) : (
                    <CustomerServiceOutlined />
                  )}
                  <div className="userPlaylistCardShade" />
                  <Button
                    className="userPlaylistCardPlay"
                    type="primary"
                    shape="circle"
                    icon={<PlayCircleOutlined />}
                    disabled={!songs.length}
                    onClick={(event) => {
                      event.stopPropagation()
                      playPlaylist(playlist)
                    }}
                    aria-label={t('playlists.play', { defaultValue: 'Play playlist' })}
                  />
                </div>

                <div className="userPlaylistCardBody">
                  <div className="userPlaylistCardTitle">{playlist.title}</div>
                  <div className="userPlaylistCardMeta">
                    {playlist.songs?.length || 0}{' '}
                    {t('playlists.songs', { defaultValue: 'songs' })}
                  </div>
                  <div className="userPlaylistCardDescription">
                    {playlist.description ||
                      t('playlists.noDescription', { defaultValue: 'No description' })}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        <div className="userPlaylistsEmptyState">
          <Empty
            description={t('playlists.empty', {
              defaultValue: 'You have not created any playlists yet.',
            })}
          />
        </div>
      )}

      <Modal
        title={t('playlists.create', { defaultValue: 'Create playlist' })}
        open={createOpen}
        confirmLoading={isSaving || isArtworkUploading}
        onCancel={closeCreateModal}
        onOk={() => void onCreatePlaylist()}
        okButtonProps={{ disabled: isArtworkUploading }}
        okText={t('playlists.create', { defaultValue: 'Create playlist' })}
      >
        <Form
          form={createForm}
          layout="vertical"
          initialValues={{ title: '', description: '' }}
        >
          <Form.Item
            name="title"
            label={t('playlists.fields.title', { defaultValue: 'Title' })}
            rules={[
              {
                required: true,
                message: t('playlists.errors.titleRequired', {
                  defaultValue: 'Title is required',
                }),
              },
            ]}
          >
            <Input
              placeholder={t('playlists.placeholders.title', {
                defaultValue: 'Playlist name',
              })}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label={t('playlists.fields.description', { defaultValue: 'Description' })}
          >
            <Input.TextArea
              rows={4}
              placeholder={t('playlists.placeholders.description', {
                defaultValue: 'Describe this playlist',
              })}
            />
          </Form.Item>

          <Form.Item label={t('playlists.fields.artwork', { defaultValue: 'Artwork' })}>
            <div className="userPlaylistCreateArtwork">
              <div className="userPlaylistCreateArtworkPreview">
                {isArtworkUploading ? (
                  <Spin />
                ) : artworkImage ? (
                  <img src={getPlaylistImageUrl(artworkImage)} alt="" />
                ) : (
                  <CustomerServiceOutlined />
                )}
              </div>
              <div>
                <div className="userPlaylistCreateArtworkTitle">
                  {t('playlists.fields.artwork', { defaultValue: 'Artwork' })}
                </div>
                <div className="userPlaylistCreateArtworkText">
                  {t('playlists.placeholders.artwork', {
                    defaultValue: 'Choose a square image for your playlist cover.',
                  })}
                </div>
                <ImgCrop
                  aspect={1}
                  rotationSlider
                  showGrid
                  quality={1}
                  modalTitle={t('playlists.actions.cropArtwork', {
                    defaultValue: 'Crop artwork',
                  })}
                >
                  <Upload {...uploadProps}>
                    <Button icon={<UploadOutlined />}>
                      {artworkImage
                        ? t('playlists.actions.changeArtwork', {
                            defaultValue: 'Change artwork',
                          })
                        : t('playlists.actions.chooseArtwork', {
                            defaultValue: 'Choose artwork',
                          })}
                    </Button>
                  </Upload>
                </ImgCrop>
              </div>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
